import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiGet, apiSend } from '../lib/api'
import { pagePath } from '../lib/pages'
import type { Agent, Credential, Customer, Device, DeviceTemplate, Site } from '../lib/types'
import { DeviceStateBadge } from '../components/Status'
import { EmptyState, ErrorBox, Loading } from '../components/Feedback'
import { NetzwerkScan } from '../components/NetzwerkScan'
import { useAuth } from '../lib/auth'

interface Props {
  customerSlug: string | null
}

/** Geräteliste mit Suche und Anlage. */
export function GeraetePage({ customerSlug }: Props) {
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const { can } = useAuth()

  const customers = useQuery({
    queryKey: ['customers'],
    queryFn: ({ signal }) => apiGet<Customer[]>('/customers', signal),
  })
  const customer = customerSlug ? customers.data?.find((c) => c.slug === customerSlug) : undefined

  const devices = useQuery({
    queryKey: ['devices', customer?.id ?? null, search],
    queryFn: ({ signal }) => {
      const params = new URLSearchParams()
      if (customer) params.set('kunde', customer.id)
      if (search.trim()) params.set('suche', search.trim())
      const query = params.toString()
      return apiGet<Device[]>(`/devices${query ? `?${query}` : ''}`, signal)
    },
    enabled: !customerSlug || Boolean(customer),
    refetchInterval: 30_000,
  })

  if (devices.isError) return <ErrorBox error={devices.error} what="Geräte" />

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">Geräte</h1>
          <div className="page-sub">
            {customer ? customer.name : 'Alle Kunden'} · {devices.data?.length ?? 0} Geräte
          </div>
        </div>
        <div className="field" style={{ minWidth: 280 }}>
          <label className="sr-only" htmlFor="geraete-suche">
            Geräte durchsuchen
          </label>
          <input
            id="geraete-suche"
            className="input"
            type="search"
            placeholder="Name, IP oder Seriennummer"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      <NewDeviceForm
        customerId={customer?.id}
        onCreated={() => queryClient.invalidateQueries({ queryKey: ['devices'] })}
      />

      {can('device.manage') ? <NetzwerkScan customerId={customer?.id} customerSlug={customerSlug} /> : null}

      <section className="card">
        <div className="card-head">
          <h2 className="card-title">Überwachte Geräte</h2>
        </div>
        {devices.isPending ? (
          <Loading what="Geräte" />
        ) : (devices.data ?? []).length === 0 ? (
          <EmptyState
            title="Noch kein Gerät"
            hint="Ein Gerät gehört immer zu einem Standort und wird von genau einem Agent abgefragt."
          />
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Gerät</th>
                <th>IP</th>
                <th>Kunde / Standort</th>
                <th>Template</th>
                <th>Agent</th>
                <th>Zustand</th>
              </tr>
            </thead>
            <tbody>
              {(devices.data ?? []).map((device) => (
                <tr key={device.id}>
                  <td>
                    <Link
                      to={`${pagePath('geraete', device.customer_slug ?? customerSlug)}/${device.id}`}
                      style={{ textDecoration: 'none', fontWeight: 600 }}
                    >
                      {device.name}
                    </Link>
                    {device.model ? (
                      <div className="muted" style={{ fontSize: 12 }}>
                        {device.model}
                      </div>
                    ) : null}
                  </td>
                  <td className="mono">{device.mgmt_ip}</td>
                  <td className="muted">
                    {device.customer_name} · {device.site_name}
                  </td>
                  <td className="muted">{device.template_key}</td>
                  <td className="muted">{device.agent_name || 'keiner'}</td>
                  <td>
                    <DeviceStateBadge state={device.state} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </>
  )
}

function NewDeviceForm({ customerId, onCreated }: { customerId?: string; onCreated: () => void }) {
  const [siteId, setSiteId] = useState('')
  const [agentId, setAgentId] = useState('')
  const [name, setName] = useState('')
  const [ip, setIP] = useState('')
  const [templateKey, setTemplateKey] = useState('generic-snmp')
  const [credentialId, setCredentialId] = useState('')
  const [snmpPort, setSNMPPort] = useState('161')

  const sites = useQuery({
    queryKey: ['sites', customerId ?? null],
    queryFn: ({ signal }) =>
      apiGet<Site[]>(customerId ? `/sites?kunde=${customerId}` : '/sites', signal),
  })
  const agents = useQuery({
    queryKey: ['agents'],
    queryFn: ({ signal }) => apiGet<Agent[]>('/agents', signal),
  })
  const templates = useQuery({
    queryKey: ['templates'],
    queryFn: ({ signal }) => apiGet<DeviceTemplate[]>('/templates', signal),
  })

  const site = sites.data?.find((s) => s.id === siteId)
  const credentials = useQuery({
    queryKey: ['credentials', site?.customer_id ?? null],
    queryFn: ({ signal }) => apiGet<Credential[]>(`/customers/${site!.customer_id}/credentials`, signal),
    enabled: Boolean(site),
  })

  const create = useMutation({
    mutationFn: async () => {
      const device = await apiSend<Device>('POST', '/devices', {
        site_id: siteId,
        agent_id: agentId || undefined,
        name: name.trim(),
        mgmt_ip: ip.trim(),
        template_key: templateKey,
        snmp_port: Number(snmpPort) || 161,
      })
      if (credentialId) {
        await apiSend<void>('PUT', `/devices/${device.id}/credentials`, {
          credential_id: credentialId,
          purpose: 'poll',
        })
      }
      return device
    },
    onSuccess: () => {
      setName('')
      setIP('')
      onCreated()
    },
  })

  const agentsForSite = (agents.data ?? []).filter((a) => !siteId || a.site_id === siteId)

  return (
    <section className="card">
      <div className="card-head">
        <h2 className="card-title">Gerät hinzufügen</h2>
      </div>
      <form
        className="card-body row"
        style={{ gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}
        onSubmit={(e) => {
          e.preventDefault()
          if (siteId && name.trim() && ip.trim()) create.mutate()
        }}
      >
        <div className="field" style={{ minWidth: 220 }}>
          <label htmlFor="geraet-standort">Standort</label>
          <select
            id="geraet-standort"
            className="input"
            value={siteId}
            onChange={(e) => {
              setSiteId(e.target.value)
              setAgentId('')
              setCredentialId('')
            }}
          >
            <option value="">Bitte wählen</option>
            {(sites.data ?? []).map((s) => (
              <option key={s.id} value={s.id}>
                {s.customer_name} · {s.name}
              </option>
            ))}
          </select>
        </div>
        <div className="field" style={{ minWidth: 180 }}>
          <label htmlFor="geraet-agent">Agent</label>
          <select
            id="geraet-agent"
            className="input"
            value={agentId}
            onChange={(e) => setAgentId(e.target.value)}
          >
            <option value="">keiner</option>
            {agentsForSite.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        </div>
        <div className="field" style={{ minWidth: 160 }}>
          <label htmlFor="geraet-name">Name</label>
          <input
            id="geraet-name"
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="fgt-wien-01"
          />
        </div>
        <div className="field" style={{ minWidth: 140 }}>
          <label htmlFor="geraet-ip">IP-Adresse</label>
          <input
            id="geraet-ip"
            className="input mono"
            value={ip}
            onChange={(e) => setIP(e.target.value)}
            placeholder="192.168.1.1"
          />
        </div>
        <div className="field" style={{ minWidth: 200 }}>
          <label htmlFor="geraet-template">Template</label>
          <select
            id="geraet-template"
            className="input"
            value={templateKey}
            onChange={(e) => setTemplateKey(e.target.value)}
          >
            {(templates.data ?? []).map((t) => (
              <option key={t.key} value={t.key}>
                {t.name}
              </option>
            ))}
          </select>
        </div>
        <div className="field" style={{ minWidth: 100 }}>
          <label htmlFor="geraet-snmpport">SNMP-Port</label>
          <input
            id="geraet-snmpport"
            className="input mono"
            value={snmpPort}
            onChange={(e) => setSNMPPort(e.target.value)}
            inputMode="numeric"
          />
        </div>
        <div className="field" style={{ minWidth: 200 }}>
          <label htmlFor="geraet-zugang">SNMP-Zugang</label>
          <select
            id="geraet-zugang"
            className="input"
            value={credentialId}
            onChange={(e) => setCredentialId(e.target.value)}
            disabled={!site}
          >
            <option value="">keiner</option>
            {(credentials.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
        <button
          type="submit"
          className="btn btn-primary"
          disabled={create.isPending || !siteId || !name.trim() || !ip.trim()}
        >
          Gerät hinzufügen
        </button>
        {create.isError ? (
          <span style={{ color: 'var(--crit)' }}>{(create.error as Error).message}</span>
        ) : null}
      </form>
    </section>
  )
}
